import React, { useContext, useState } from 'react';

import PubSub from 'pubsub-js';
import { useTranslation } from 'react-i18next';
import { useHistory, useLocation } from 'react-router';
import { Button } from '@material-ui/core';
import { Link } from 'react-router-dom';
import PageContent from 'components/Generic/PageContent';
import { Routes } from 'routes';
import { EstimatedReceptionForm, Event, UserRole } from 'types';
import { AppContext } from 'context';
import BottomButtonBar from 'components/Generic/BottomButtonBar';
import EstimatedReceptionEditForm from 'components/Forms/EstimatedReceptionEditForm';
import { notify } from 'components/Notifier';
import { ValidateIcon } from 'components/Icons';
import { putEstimatedReceptions } from 'services/apiOperations';
import { formatEstimatedReceptionsBeforePut } from 'utils/normalizer';

const EstimatedReceptionListPage: React.FC = () => {
  const { t } = useTranslation();
  const history = useHistory();
  const location = useLocation();
  const isInEditMode = location.pathname === Routes.EstimatedReceptionsEdit;
  const context = useContext(AppContext);
  const roles = context.getRoles?.();
  const sortingCenter = context.getSortingCenter?.();

  const userCanEdit =
    sortingCenter?.isActive && roles && (roles.includes(UserRole.ROLE_MANAGER) || context.hasLeastOneAdminRole());
  const [isSubmitting, setIsSubmitting] = useState(false);

  const onSubmit = async (estimatedReceptions: EstimatedReceptionForm) => {
    if (isSubmitting) {
      return;
    }

    const sortingCenterId = sortingCenter?.['@id'] ?? null;

    if (null === sortingCenterId) {
      notify({
        message: t('estimatedReceptions.creation.error'),
        severity: 'error',
      });
      return;
    }

    const formattedEstimatedReceptions = formatEstimatedReceptionsBeforePut(sortingCenterId, estimatedReceptions);

    setIsSubmitting(true);

    try {
      await putEstimatedReceptions(formattedEstimatedReceptions);
      notify({
        message: t('estimatedReceptions.edit.success'),
        severity: 'success',
      });
      history.push(Routes.EstimatedReceptions);
      setIsSubmitting(false);
      PubSub.publish(Event.REFRESH_ESTIMATED_RECEPTIONS);
    } catch (e) {
      notify({
        message: t('errorGeneric'),
        severity: 'error',
      });
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <PageContent
        title={t('estimatedReceptions.entityName')}
        noPadding
        customAction={() => (
          <>
            {!isInEditMode && userCanEdit && (
              <Button component={Link} to={Routes.EstimatedReceptionsEdit} variant="outlined" color="default">
                {t('update')}
              </Button>
            )}
          </>
        )}
      >
        <EstimatedReceptionEditForm onSubmit={onSubmit} readOnly={!isInEditMode} />
      </PageContent>
      {isInEditMode && (
        <BottomButtonBar
          disableNext={isSubmitting}
          previousButtonLink={Routes.EstimatedReceptions}
          formName="estimated_receptions_form"
          previousStepLabel={t('cancel')}
          nextStepLabel={t('save')}
          nextStepIcon={<ValidateIcon />}
        />
      )}
    </>
  );
};
export default EstimatedReceptionListPage;
